// Client for the hero slides API (api/hero_slides.php) — admin-manageable
// Home page hero carousel slides. Each slide's background image lives in
// media_slots under slot key `home.hero.<id>`.

import { parseJsonResponse } from './apiFetch';
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

export interface HeroSlide {
  id: number;
  title: string;
  subtitle: string;
  primary_text: string;
  primary_link: string;
  secondary_text: string;
  secondary_link: string;
  sort_order: number;
}

export interface HeroSlideInput {
  title: string;
  subtitle: string;
  primary_text: string;
  primary_link: string;
  secondary_text: string;
  secondary_link: string;
  sort_order: number;
}

export function heroSlideImageSlotKey(id: number): string {
  return `home.hero.${id}`;
}

// Admin-only (the guest site's hero gets its data from the combined
// bootstrap endpoint instead), so this always cache-busts — see the
// matching comment on fetchRooms() in roomsApi.ts.
export async function fetchHeroSlides(): Promise<HeroSlide[]> {
  const res = await fetch(`${API_BASE}/hero_slides.php?action=list&v=${Date.now()}`);
  const data = await parseJsonResponse(res);
  if (!data.success) throw new Error(data.message ?? 'Failed to load hero slides');
  return data.slides;
}

export async function addHeroSlide(
  input: HeroSlideInput,
  csrfToken: string,
): Promise<{ success: boolean; id?: number; message?: string }> {
  const res = await fetch(`${API_BASE}/hero_slides.php?action=add`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function updateHeroSlide(
  id: number,
  input: HeroSlideInput,
  csrfToken: string,
): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/hero_slides.php?action=update`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, ...input, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function deleteHeroSlide(id: number, csrfToken: string): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/hero_slides.php?action=delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}
